'use client';

import type { ReactNode, JSX } from 'react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

import { isScrolledIntoView } from 'utils/device';

const MOVEMENT_IN_DURATION_MS = 400;
const MOVEMENT_OUT_DURATION_MS = 1200;

const easeInBack = 'ease-[cubic-bezier(0.36,0,0.66,-0.56)]';
const easeOutBack = 'ease-[cubic-bezier(0.34,1.56,0.64,1)]';

const styles = {
    upAnimationExitingClass: `[&_span]:!text-green-x10 [&_span]:transition-all [&_span]:${easeInBack}`,
    downAnimationExitingClass: `[&_span]:!text-red-x10 [&_span]:transition-all [&_span]:${easeInBack}`,
    loadingAnimationExitingClass: `[&_span]:!text-text-x30 [&_span]:transition-all [&_span]:${easeInBack}`,
    up: `[&_span]:!text-green-x10 [&_span]:!opacity-100 [&_span]:transition-all [&_span]:${easeOutBack}`,
    down: `[&_span]:!text-red-x10 [&_span]:!opacity-100 [&_span]:transition-all [&_span]:${easeOutBack}`,
    loading: `[&_span]:!text-text-x30 [&_span]:!opacity-40 [&_span]:transition-all [&_span]:${easeOutBack}`,
};

interface NumberChangeAnimationWrapperProps {
    number: number;
    animate?: boolean;
    scrollAnimate?: boolean;
    price?: boolean;
    externalTrigger?: boolean;
    children: ReactNode;
    style?: React.CSSProperties;
    className?: string;
    movementInDurationMs?: number;
    movementOutDurationMs?: number;
}

type AnimationState = 'up' | 'down' | 'loading' | null;
type ExitAnimationState =
    | 'upAnimationExitingClass'
    | 'downAnimationExitingClass'
    | 'loadingAnimationExitingClass'
    | null;

const NumberChangeAnimationWrapper = ({
    number,
    animate = true,
    scrollAnimate = false,
    price = true,
    externalTrigger = false,
    children,
    style,
    className,
    movementInDurationMs = MOVEMENT_IN_DURATION_MS,
    movementOutDurationMs = MOVEMENT_OUT_DURATION_MS,
}: NumberChangeAnimationWrapperProps): JSX.Element => {
    const [timeoutCanAnimate, setTimeoutCanAnimate] = useState(false);

    const [animationState, setAnimationState] = useState<AnimationState>(null);
    const [addAnimationExitingClass, setAddAnimationExitingClass] = useState<ExitAnimationState>(null);
    const [alreadyScrollAnimated, setAlreadyScrollAnimated] = useState(false);
    const [scrolling, setScrolling] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);

    const prevNumber = useRef<number>(number);
    const inputRef = useRef<HTMLSpanElement>(null);
    // The in-flight flash's pending timer (its in phase, then its exit phase). A number that changes faster than
    // the flash lasts restarts it rather than stacking another on top: stacked, every change queued two timers,
    // each a separate re-render, and their overlapping class flips fought each other.
    const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearFlashTimer = (): void => {
        if (flashTimer.current) clearTimeout(flashTimer.current);
    };
    useEffect(() => clearFlashTimer, []);

    const triggerAnimation = (direction: AnimationState, exitDirection: ExitAnimationState): void => {
        if (!timeoutCanAnimate || !animate || direction === null) return;

        clearFlashTimer();
        setAddAnimationExitingClass(null);
        setAnimationState(direction);
        flashTimer.current = setTimeout(() => {
            setAnimationState(null);
            setAddAnimationExitingClass(exitDirection);
            flashTimer.current = setTimeout(() => setAddAnimationExitingClass(null), movementOutDurationMs);
        }, movementInDurationMs);
    };

    const triggerLoadingAnimation = (): void => {
        triggerAnimation('loading', 'loadingAnimationExitingClass');
    };

    useEffect(() => {
        // We use a timeout so that there is a delay before the animation is triggered
        let timeout: NodeJS.Timeout | undefined;
        if (animate) {
            timeout = setTimeout(() => setTimeoutCanAnimate(true), 1000);
        } else {
            setTimeoutCanAnimate(false);
        }

        return (): void => {
            if (!!timeout) clearTimeout(timeout);
        };
    }, [animate]);

    useEffect(() => {
        const onScroll = (e: any): void => {
            if (!e.target.documentElement) return;
            setScrollTop(e.target.documentElement.scrollTop);
            setScrolling(e.target.documentElement.scrollTop > scrollTop);
        };
        window.addEventListener('scroll', onScroll);

        return (): void => window.removeEventListener('scroll', onScroll);
    }, [scrollTop]);

    useEffect(() => {
        if (inputRef?.current && isScrolledIntoView(inputRef?.current) && !alreadyScrollAnimated && scrollAnimate) {
            if (price && number < 50) {
                triggerAnimation('down', 'downAnimationExitingClass');
            } else if (price && number > 49) {
                triggerAnimation('up', 'upAnimationExitingClass');
            } else {
                triggerLoadingAnimation();
            }
            setAlreadyScrollAnimated(true);
        }
        if (inputRef?.current && !isScrolledIntoView(inputRef?.current)) {
            setAlreadyScrollAnimated(false);
        }
    }, [scrollTop, scrolling]);

    useEffect(() => {
        if (externalTrigger == false) return;

        if (price && number < 50) {
            triggerAnimation('down', 'downAnimationExitingClass');
        } else if (price && number > 49) {
            triggerAnimation('up', 'upAnimationExitingClass');
        } else {
            triggerLoadingAnimation();
        }
    }, [externalTrigger]);

    useEffect(() => {
        if (number > prevNumber.current) triggerAnimation('up', 'upAnimationExitingClass');
        else if (number < prevNumber.current) triggerAnimation('down', 'downAnimationExitingClass');
        prevNumber.current = number;
    }, [number]);

    return (
        <span
            ref={inputRef}
            style={style}
            className={clsx(
                'inline-flex items-center',
                className,
                animationState && styles[animationState],
                addAnimationExitingClass && styles[addAnimationExitingClass],
            )}
        >
            {children}
        </span>
    );
};

export default NumberChangeAnimationWrapper;
